// src/pages/VacationRequests/HrAdminAllVacationRequestsPage.jsx

import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  Chip,
} from '@mui/material';

import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import HistoryIcon from '@mui/icons-material/History';
import { FlightTakeoffRounded } from '@mui/icons-material';

// Pages/Components
import HrAdminVacationRequestsPage from './HrAdminVacationRequestsPage';
import MyVacationRequests from './MyVacationRequests';
import VacationBalanceList from '../VacationBalance/VacationBalanceList';
import VacationBalanceHistoryPage from '../VacationBalance/VacationBalanceHistoryPage';
import ExitEntryListPage from '../ExitEntry/ExitEntryListPage';

/** A11y helpers */
function a11yProps(index) {
  return {
    id: `hr-admin-tab-${index}`,
    'aria-controls': `hr-admin-tabpanel-${index}`,
  };
}

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hr-admin-tabpanel-${index}`}
      aria-labelledby={`hr-admin-tab-${index}`}
      style={{ height: '100%' }}
    >
      {value === index && <Box sx={{ py: 1 }}>{children}</Box>}
    </div>
  );
}

function HrAdminAllVacationRequestsPage() {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);

  // Optional: deep-link support via URL hash (#tab=0 … #tab=4)
  const LAST_TAB_INDEX = 4;
  useEffect(() => {
    const url = new URL(window.location.href);
    const hashTab = url.hash.match(/tab=(\d+)/)?.[1];
    if (hashTab) {
      const idx = Number(hashTab);
      if (!Number.isNaN(idx)) {
        setActiveTab(Math.min(Math.max(idx, 0), LAST_TAB_INDEX));
      }
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.hash = `tab=${activeTab}`;
    // Don’t push history infinitely on initial mount
    if (window.history.replaceState) {
      window.history.replaceState(null, '', url.toString());
    }
  }, [activeTab]);

  const handleTabChange = (_e, newValue) => setActiveTab(newValue);

  const palette = theme.palette;
  const styles = useMemo(() => {
    const primary = palette.primary.main;
    const bgSoft =
      theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(23,32,42,0.7) 0%, rgba(23,32,42,0.3) 60%, rgba(23,32,42,0.0) 100%)'
        : 'linear-gradient(135deg, #f0f4ff 0%, #ffffff 70%)';

    const headerShadow =
      theme.palette.mode === 'dark'
        ? '0 6px 24px rgba(0,0,0,0.35)'
        : '0 6px 24px rgba(0,0,0,0.08)';

    return {
      headerBg: bgSoft,
      headerShadow,
      primary,
    };
  }, [palette, theme.palette.mode]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Heading & Subtitle */}
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          mb: 3,
          p: { xs: 2, sm: 3 },
          background: styles.headerBg,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: styles.headerShadow,
          backdropFilter: 'blur(3px)',
        }}
      >
        <Typography variant={downSm ? 'h5' : 'h4'} fontWeight={700}>
          HR Admin Vacation Panel
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, maxWidth: 820, mx: 'auto' }}
        >
          Review and finalize assigned vacation requests, manage your own
          requests, and track vacation balances & history in one place.
        </Typography>

        {/* Helpful quick chips */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mt: 2, flexWrap: 'wrap' }}
        >
          <Chip size="small" label="Role: HR Admin" />
          <Chip size="small" label="Actions: Review • Approve • Track" />
          <Chip size="small" label="Views: Requests • Balances • History" />
        </Stack>
      </Paper>

      {/* Main container for Tabs & Pages */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.02)'
              : palette.background.paper,
        }}
      >
        {/* Sticky, pill-style, scrollable tabs */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(18,18,18,0.6)'
                : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(6px)',
            borderRadius: 2,
            px: { xs: 0.5, sm: 1 },
            pt: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            // Scrollable on mobile, full width on larger screens
            variant={downSm ? 'scrollable' : 'fullWidth'}
            scrollButtons={downSm ? 'auto' : false}
            textColor="primary"
            indicatorColor="primary"
            TabIndicatorProps={{
              sx: {
                height: 0, // hide default line; we’ll style the selected tab instead
              },
            }}
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 1 },
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 44,
                borderRadius: 999,
                fontWeight: 600,
                letterSpacing: 0.2,
                px: { xs: 1.5, sm: 2.25 },
                mx: 0.25,
                transition: 'all .2s ease',
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.04)',
                },
                '&.Mui-selected': {
                  color: styles.primary,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(99,102,241,0.12)'
                      : 'rgba(25,118,210,0.10)',
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? 'inset 0 0 0 1px rgba(99,102,241,0.25)'
                      : 'inset 0 0 0 1px rgba(25,118,210,0.18)',
                },
              },
            }}
          >
            <Tab
              icon={<SupervisorAccountIcon fontSize="small" />}
              iconPosition="start"
              label="Assigned Vacation Requests"
              disableRipple
              {...a11yProps(0)}
            />
            <Tab
              icon={<PersonOutlineIcon fontSize="small" />}
              iconPosition="start"
              label="My Requests"
              disableRipple
              {...a11yProps(1)}
            />
            <Tab
              icon={<CardTravelIcon fontSize="small" />}
              iconPosition="start"
              label="Vacation Balances"
              disableRipple
              {...a11yProps(2)}
            />
            <Tab
              icon={<HistoryIcon fontSize="small" />}
              iconPosition="start"
              label="Vacation History"
              disableRipple
              {...a11yProps(3)}
            />
            <Tab
              icon={<FlightTakeoffRounded fontSize="small" />}
              iconPosition="start"
              label="Exit/Entry"
              disableRipple
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>

        {/* Animated content area */}
        <Box sx={{ mt: 2, pb: 1 }}>
          <Fade in timeout={200} key={activeTab}>
            <Box>
              <TabPanel value={activeTab} index={0}>
                <HrAdminVacationRequestsPage />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <MyVacationRequests />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <VacationBalanceList />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <VacationBalanceHistoryPage />
              </TabPanel>
              <TabPanel value={activeTab} index={4}>
                <ExitEntryListPage />
              </TabPanel>
            </Box>
          </Fade>
        </Box>
      </Paper>
    </Box>
  );
}

export default HrAdminAllVacationRequestsPage;
